/**
 * STICKER GENERATOR V5 - AVISOCAR PREMIUM PRO
 * ============================================
 * Generates beautiful, premium-quality stickers using Canvas API + jsPDF.
 * 
 * V5 Features:
 * - Perfectly centered QR with safe margins
 * - Fixed footer (AvisoCar.com - NOT editable)
 * - Auto-fit text sizing
 * - Better layout alignment
 * - Icon positioning options
 * - Enhanced backgrounds
 * 
 * CRITICAL: Always fetches /api/qr/latest before generating to ensure
 * the QR is up-to-date. NO CACHE allowed.
 */

import { jsPDF } from "jspdf";
import { pdfStrings } from "./sticker_pdf_i18n";
import { getPalette, PALETTES } from "../data/stickerTemplates";
import { 
  STICKER_FOOTER, 
  getStickerFooter,
  TEXT_SIZES, 
  calculateAutoFitSize, 
  generateGradientCSS 
} from "./sticker_styles_engine_v5";

const API = '';

// ============================================
// QR DATA FETCHING (ANTI-CACHE)
// ============================================

/**
 * Fetch the latest QR data from backend (anti-cache)
 * Returns full qr_url for display in frontend
 */
export const fetchLatestQR = async (vehicleId) => {
  const timestamp = Date.now();
  const url = vehicleId 
    ? `${API}/api/qr/latest?vehicle_id=${vehicleId}&t=${timestamp}`
    : `${API}/api/qr/latest?t=${timestamp}`;
  
  const response = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${localStorage.getItem("avisocar_token")}`,
      "Cache-Control": "no-store"
    }
  });
  
  if (!response.ok) {
    throw new Error("Error fetching QR data");
  }
  
  const data = await response.json();
  
  // V5 FIX: Build full qr_url for image display
  // The backend returns qr_image_url as relative path, we need to make it absolute
  if (data.qr_token && !data.qr_url?.includes("/image")) {
    data.qr_url = `${API}/api/public/qr/${data.qr_token}/image`;
  } else if (data.qr_image_url) {
    data.qr_url = `${API}${data.qr_image_url}`;
  }
  
  return data;
};

/**
 * Load QR image as Image element with anti-cache
 */
const loadQRImage = (qrToken) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timestamp = Date.now();
    img.src = `${API}/api/public/qr/${qrToken}/image?t=${timestamp}`;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load QR image"));
  });
};

// ============================================
// CANVAS DRAWING HELPERS
// ============================================

/**
 * Draw rounded rectangle path
 */
const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

/**
 * Parse color to ensure valid format
 */
const parseColor = (color) => {
  if (!color) return "#10B981";
  return color;
};

/**
 * Lighten a hex color
 */
const lightenColor = (hex, factor) => {
  if (!hex || hex.length < 7) return "#FFFFFF";
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    const newR = Math.min(255, Math.round(r + (255 - r) * factor));
    const newG = Math.min(255, Math.round(g + (255 - g) * factor));
    const newB = Math.min(255, Math.round(b + (255 - b) * factor));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  } catch {
    return "#FFFFFF";
  }
};

/**
 * Darken a hex color
 */
const darkenColor = (hex, factor) => {
  if (!hex || hex.length < 7) return "#000000";
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    const newR = Math.round(r * (1 - factor));
    const newG = Math.round(g * (1 - factor));
    const newB = Math.round(b * (1 - factor));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  } catch {
    return "#000000";
  }
};

// ============================================
// BACKGROUND RENDERERS
// ============================================

/**
 * Draw premium background based on template style
 */
const drawBackground = (ctx, size, template, palette, styleOptions = {}) => {
  const primary = parseColor(palette?.primary || "#10B981");
  const secondary = parseColor(palette?.secondary || "#6EE7B7");
  const bgType = template?.background?.type || "gradient";
  
  // Use custom gradient if provided
  const customGradient = styleOptions.backgroundGradient;
  
  // Outer rounded frame
  ctx.save();
  roundRect(ctx, 0, 0, size, size, 24);
  ctx.clip();
  
  // Main background
  if (customGradient && customGradient.colors) {
    // Custom gradient from style options
    const colors = customGradient.colors;
    const angle = customGradient.angle || 135;
    
    // Convert angle to coordinates
    const angleRad = (angle - 90) * Math.PI / 180;
    const x1 = size / 2 - Math.cos(angleRad) * size / 2;
    const y1 = size / 2 - Math.sin(angleRad) * size / 2;
    const x2 = size / 2 + Math.cos(angleRad) * size / 2;
    const y2 = size / 2 + Math.sin(angleRad) * size / 2;
    
    const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
    colors.forEach((color, i) => {
      gradient.addColorStop(i / (colors.length - 1), color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  } else if (bgType === "solid") {
    ctx.fillStyle = template?.background?.color || primary;
    ctx.fillRect(0, 0, size, size);
  } else if (bgType === "split") {
    // Two-tone split
    const colors = template?.background?.colors || [primary, secondary];
    ctx.fillStyle = colors[0];
    ctx.fillRect(0, 0, size, size * 0.5);
    ctx.fillStyle = colors[1];
    ctx.fillRect(0, size * 0.5, size, size * 0.5);
  } else {
    // Gradient background (default)
    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, primary);
    gradient.addColorStop(1, lightenColor(primary, 0.3));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }
  
  // Pattern overlay
  const pattern = template?.background?.pattern;
  if (pattern) {
    drawPattern(ctx, size, pattern, palette);
  }
  
  ctx.restore();
  
  // Draw frame if enabled
  if (styleOptions.frameEnabled !== false) {
    drawFrame(ctx, size, template?.layout || "hero", palette, styleOptions.glowEnabled);
  }
};

/**
 * Draw decorative patterns
 */
const drawPattern = (ctx, size, pattern, palette) => {
  ctx.globalAlpha = 0.15;
  
  switch (pattern) {
    case "dots":
      for (let i = 0; i < 20; i++) {
        const x = Math.random() * size;
        const y = Math.random() * size;
        ctx.beginPath();
        ctx.arc(x, y, 4 + Math.random() * 8, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
      }
      break;
      
    case "diagonal-lines":
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      for (let i = -size; i < size * 2; i += 20) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + size, size);
        ctx.stroke();
      }
      break;
      
    case "warning-stripes":
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "#000000";
      for (let i = -size; i < size * 2; i += 30) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + 15, 0);
        ctx.lineTo(i + size + 15, size);
        ctx.lineTo(i + size, size);
        ctx.closePath();
        ctx.fill();
      }
      break;
      
    case "stars":
    case "sparkles":
      for (let i = 0; i < 15; i++) {
        const x = Math.random() * size;
        const y = Math.random() * size * 0.4;
        drawStar(ctx, x, y, 3 + Math.random() * 5, "#FFFFFF");
      }
      break;
      
    case "paws":
      ctx.globalAlpha = 0.1;
      for (let i = 0; i < 8; i++) {
        drawPaw(ctx, Math.random() * size, Math.random() * size, 15 + Math.random() * 10);
      }
      break;
      
    case "hearts":
      ctx.globalAlpha = 0.12;
      for (let i = 0; i < 10; i++) {
        drawHeart(ctx, Math.random() * size, Math.random() * size, 10 + Math.random() * 8);
      }
      break;
      
    case "clouds":
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = "#FFFFFF";
      for (let i = 0; i < 5; i++) {
        drawCloud(ctx, Math.random() * size, Math.random() * size * 0.3, 30 + Math.random() * 20);
      }
      break;
      
    default:
      break;
  }
  
  ctx.globalAlpha = 1;
};

/**
 * Draw premium frame based on layout
 */
const drawFrame = (ctx, size, layout, palette, glowEnabled = false) => {
  const primary = parseColor(palette?.primary || "#10B981");
  const accent = parseColor(palette?.accent || "#FFFFFF");
  
  // Outer glow effect
  if (glowEnabled) {
    ctx.shadowColor = primary;
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
  
  // Frame border
  ctx.strokeStyle = darkenColor(primary, 0.2);
  ctx.lineWidth = 6;
  roundRect(ctx, 3, 3, size - 6, size - 6, 22);
  ctx.stroke();
  
  // Inner border
  ctx.shadowBlur = 0;
  ctx.strokeStyle = accent + "40";
  ctx.lineWidth = 2;
  roundRect(ctx, 10, 10, size - 20, size - 20, 18);
  ctx.stroke();
  
  // Layout-specific decorations
  if (layout === "warning") {
    // Warning corners
    ctx.fillStyle = "#FBBF24";
    const cornerSize = 30;
    
    // Top-left corner triangle
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(cornerSize, 0);
    ctx.lineTo(0, cornerSize);
    ctx.closePath();
    ctx.fill();
    
    // Top-right corner
    ctx.beginPath();
    ctx.moveTo(size, 0);
    ctx.lineTo(size - cornerSize, 0);
    ctx.lineTo(size, cornerSize);
    ctx.closePath();
    ctx.fill();
  } else if (layout === "badge") {
    // Badge ribbon effect at bottom
    ctx.fillStyle = darkenColor(primary, 0.3);
    ctx.beginPath();
    ctx.moveTo(size * 0.3, size);
    ctx.lineTo(size * 0.35, size - 20);
    ctx.lineTo(size * 0.65, size - 20);
    ctx.lineTo(size * 0.7, size);
    ctx.closePath();
    ctx.fill();
  }
};

// Pattern helper functions
const drawStar = (ctx, x, y, size, color) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
    const px = x + size * Math.cos(angle);
    const py = y + size * Math.sin(angle);
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
};

const drawPaw = (ctx, x, y, size) => {
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.ellipse(x, y, size * 0.6, size * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  for (let i = 0; i < 4; i++) {
    const angle = Math.PI + (i - 1.5) * 0.4;
    const tx = x + size * 0.7 * Math.cos(angle);
    const ty = y + size * 0.7 * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(tx, ty, size * 0.25, 0, Math.PI * 2);
    ctx.fill();
  }
};

const drawHeart = (ctx, x, y, size) => {
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.moveTo(x, y + size * 0.3);
  ctx.bezierCurveTo(x, y, x - size, y, x - size, y + size * 0.3);
  ctx.bezierCurveTo(x - size, y + size * 0.6, x, y + size, x, y + size);
  ctx.bezierCurveTo(x, y + size, x + size, y + size * 0.6, x + size, y + size * 0.3);
  ctx.bezierCurveTo(x + size, y, x, y, x, y + size * 0.3);
  ctx.fill();
};

const drawCloud = (ctx, x, y, size) => {
  ctx.beginPath();
  ctx.arc(x, y, size * 0.5, 0, Math.PI * 2);
  ctx.arc(x + size * 0.4, y - size * 0.2, size * 0.4, 0, Math.PI * 2);
  ctx.arc(x + size * 0.8, y, size * 0.5, 0, Math.PI * 2);
  ctx.fill();
};

// ============================================
// TEXT RENDERING (V5 Enhanced)
// ============================================

/**
 * Draw text with shadow and proper styling (V5: with alignment support)
 */
const drawText = (ctx, text, x, y, options = {}) => {
  const {
    fontSize = 24,
    fontWeight = "bold",
    color = "#FFFFFF",
    shadow = true,
    align = "center",
    maxWidth = null
  } = options;
  
  ctx.font = `${fontWeight} ${fontSize}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  
  if (shadow) {
    ctx.shadowColor = "rgba(0,0,0,0.3)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
  }
  
  ctx.fillStyle = color;
  
  if (maxWidth) {
    ctx.fillText(text, x, y, maxWidth);
  } else {
    ctx.fillText(text, x, y);
  }
  
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

/**
 * V5: Draw icons at specified position
 */
// V6.4: Draw icons with support for normalized positions from layoutOffsets
const drawIcons = (ctx, size, icons = [], position = "top-right", palette, layoutOffsets = null) => {
  if (!icons || icons.length === 0) return;
  
  // V6.3: Complete icon map matching editor's PREMIUM_ICONS
  const iconEmojis = icons.map(iconId => {
    const iconData = {
      // Vehiculos
      car: "🚗", tow: "🚛", parking: "🅿️", lights: "🚨", alarm: "🔔",
      // Niños
      child: "🧒", family: "👨‍👩‍👧", school: "🎒", park: "🎠", bracelet: "📿",
      // Mascotas
      paw: "🐾", pet: "🐾", collar: "🦮", pet_home: "🏠", vet: "💉", bone: "🦴",
      // Objetos
      luggage: "🧳", airport: "✈️", backpack: "🎒", keys: "🔑", wallet: "👛",
      // Viajes
      plane: "✈️", passport: "🛂", map: "🗺️", hotel: "🏨", ticket: "🎫",
      // Emergencias
      sos: "🆘", emergency: "🆘", ambulance: "🚑", crash: "💥", police: "🚔", fire: "🔥",
      // Privacidad
      lock: "🔒", shield: "🛡️", anonymous: "🎭", eye_off: "🙈", secure: "🔐",
      // Legacy icons
      motorcycle: "🏍️", truck: "🚚", warning: "⚠️", heart: "❤️",
      phone: "📱", star: "⭐", bell: "🔔", key: "🔑"
    };
    return iconData[iconId] || "⭐";
  });
  
  // V6.4: Calculate icon position from layoutOffsets if available
  let startX, startY, iconSize, spacing;
  
  if (layoutOffsets?.icons && (layoutOffsets.icons.xPercent !== undefined || layoutOffsets.icons.x !== undefined)) {
    // V6.4: Use saved positions
    const iconOffsets = layoutOffsets.icons;
    
    if (iconOffsets.xPercent !== undefined && iconOffsets.yPercent !== undefined) {
      // Use normalized percentages
      startX = iconOffsets.xPercent * size;
      startY = iconOffsets.yPercent * size;
      iconSize = iconOffsets.widthPercent ? Math.max(20, iconOffsets.widthPercent * size / icons.length) : 24;
    } else {
      // Legacy pixel positions - scale from 420px to export size
      const scaleFactor = size / 420;
      startX = iconOffsets.x * scaleFactor;
      startY = iconOffsets.y * scaleFactor;
      iconSize = iconOffsets.width ? (iconOffsets.width * scaleFactor / icons.length) : 24;
    }
    
    spacing = iconSize * 0.5;
    ctx.font = `${Math.max(18, iconSize)}px system-ui`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    
    // Draw icons at saved position
    iconEmojis.forEach((emoji, i) => {
      ctx.fillText(emoji, startX + i * (iconSize + spacing), startY + iconSize / 2);
    });
  } else {
    // V6.4: Fallback to position-based rendering
    iconSize = 24;
    const padding = 25;
    spacing = 12;
    const totalWidth = icons.length * (iconSize + spacing) - spacing;
    
    ctx.font = `${iconSize}px system-ui`;
    ctx.textBaseline = "middle";
    
    switch (position) {
      case "top-left":
        startX = padding + iconSize / 2;
        startY = padding + iconSize / 2;
        ctx.textAlign = "left";
        iconEmojis.forEach((emoji, i) => {
          ctx.fillText(emoji, startX + i * (iconSize + spacing), startY);
        });
        break;
      case "top-center":
        startX = (size - totalWidth) / 2 + iconSize / 2;
        startY = padding + iconSize / 2;
        ctx.textAlign = "center";
        iconEmojis.forEach((emoji, i) => {
          ctx.fillText(emoji, startX + i * (iconSize + spacing), startY);
        });
        break;
      case "top-right":
      default:
        startX = size - padding - iconSize / 2;
        startY = padding + iconSize / 2;
        ctx.textAlign = "right";
        iconEmojis.slice().reverse().forEach((emoji, i) => {
          ctx.fillText(emoji, startX - i * (iconSize + spacing), startY);
        });
        break;
    }
  }
};

// ============================================
// QR CODE CARD (V5: Perfectly Centered)
// ============================================

/**
 * V5: Draw QR code in a premium card with PERFECT centering
 */
const drawQRCard = async (ctx, size, qrToken, layout) => {
  // V5: QR takes 40% of total size for better proportions
  const qrSize = Math.floor(size * 0.40);
  const padding = 12;
  const cardSize = qrSize + padding * 2;
  
  // V5: PERFECT CENTER calculation
  const qrX = Math.floor((size - qrSize) / 2);
  const qrY = Math.floor(size * 0.38); // Adjusted Y position
  const cardX = qrX - padding;
  const cardY = qrY - padding;
  
  // Card shadow
  ctx.shadowColor = "rgba(0,0,0,0.25)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 8;
  
  // White card background
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, cardX, cardY, cardSize, cardSize, 16);
  ctx.fill();
  
  // Reset shadow
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  
  // Inner border
  ctx.strokeStyle = "#E2E8F0";
  ctx.lineWidth = 1;
  roundRect(ctx, cardX + 2, cardY + 2, cardSize - 4, cardSize - 4, 14);
  ctx.stroke();
  
  // Draw QR code
  try {
    const qrImg = await loadQRImage(qrToken);
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  } catch (e) {
    // Fallback QR placeholder
    ctx.fillStyle = "#E2E8F0";
    ctx.fillRect(qrX, qrY, qrSize, qrSize);
    ctx.fillStyle = "#64748B";
    ctx.font = "bold 20px system-ui";
    ctx.textAlign = "center";
    ctx.fillText("QR", qrX + qrSize / 2, qrY + qrSize / 2);
  }
  
  return { qrX, qrY, qrSize, cardY: cardY + cardSize };
};

// ============================================
// V5: FIXED FOOTER (NOT EDITABLE CONTENT)
// ============================================

/**
 * V5.2: Draw fixed AvisoCar branding footer - BIGGER SIZE
 * Content is FIXED and NOT EDITABLE by users
 * Only style (size, color, weight) can be customized
 */
const drawFooter = (ctx, size, palette, footerSize = "medium") => {
  const accent = parseColor(palette?.accent || "#FFFFFF");
  
  // V5.2: LARGER FOOTER SIZES
  const sizeMap = {
    small: 14,
    medium: 18,
    large: 22
  };
  const baseFontSize = sizeMap[footerSize] || sizeMap.medium;
  
  // Footer content - FIXED (CORTE 4-C: line2 en idioma del dueño)
  const _footerLang = (typeof localStorage !== "undefined" && (localStorage.getItem("avisocar_lang") || localStorage.getItem("avisocar_account_lang") || localStorage.getItem("avisocar_geo_lang"))) || "es";
  const line1 = STICKER_FOOTER.line1;
  const line2 = getStickerFooter(_footerLang).line2;
  
  ctx.textAlign = "center";
  
  // V5.2: Gradient background for footer area
  ctx.fillStyle = "rgba(0,0,0,0.15)";
  ctx.fillRect(0, size - 55, size, 55);
  
  // Line 1: "AvisoCar.com" - BIGGER, bold
  ctx.font = `bold ${baseFontSize + 4}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = accent;
  const line1Y = size - 32;
  ctx.fillText(`🚗 ${line1}`, size / 2, line1Y);
  
  // Line 2: Description - slightly larger
  ctx.font = `600 ${baseFontSize - 4}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = accent + "DD";
  const line2Y = size - 14;
  
  // Handle long text with ellipsis
  const maxWidth = size - 40;
  ctx.fillText(line2, size / 2, line2Y, maxWidth);
};

// ============================================
// MAIN STICKER CANVAS CREATION (V5)
// ============================================

/**
 * V5: Create premium sticker canvas with full visual design
 */
const createStickerCanvas = async (options) => {
  const {
    template,
    qrToken,
    customSlogan,
    customTitle,
    customSubtitle,
    styleOptions = {},
    size = 600,
  } = options;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  
  // High resolution for print quality
  const scale = 2;
  const actualSize = size * scale;
  canvas.width = actualSize;
  canvas.height = actualSize;
  ctx.scale(scale, scale);

  // Get palette
  const palette = getPalette(template?.palette) || PALETTES.emerald_forest || {
    primary: "#10B981",
    secondary: "#6EE7B7",
    accent: "#FFFFFF"
  };
  const layout = template?.layout || "hero";
  const accent = parseColor(palette.accent);
  const primaryText = styleOptions.primaryTextColor?.value || accent;
  const secondaryText = styleOptions.secondaryTextColor?.value || (accent + "DD");
  
  // 1. Draw background with patterns and frame
  drawBackground(ctx, size, template, palette, styleOptions);
  
  // V6.6: FIXED Z-ORDER - Draw texts BEFORE QR so they don't overlap
  // V6.2: Text size maps for export rendering
  const TEXT_SIZE_EXPORT_MAP = {
    title: { S: 28, M: 36, L: 44, XL: 54 },
    subtitle: { S: 20, M: 24, L: 28 },
    slogan: { S: 22, M: 26, L: 32 }
  };
  
  // 2. Draw title (V5.2: with configurable size)
  const title = customTitle || styleOptions.title || template?.title || "";
  if (title) {
    const titleSizeKey = styleOptions.titleSize || "L";
    const titleFontSize = TEXT_SIZE_EXPORT_MAP.title[titleSizeKey] || TEXT_SIZE_EXPORT_MAP.title.L;
    drawText(ctx, title, size / 2, size * 0.12, {
      fontSize: titleFontSize,
      fontWeight: "800",
      color: primaryText,
      shadow: true
    });
  }
  
  // 3. Draw subtitle (V5.2: with configurable size)
  const subtitle = customSubtitle || styleOptions.subtitle || template?.subline || "";
  if (subtitle) {
    const subtitleSizeKey = styleOptions.subtitleSize || "M";
    const subtitleFontSize = TEXT_SIZE_EXPORT_MAP.subtitle[subtitleSizeKey] || TEXT_SIZE_EXPORT_MAP.subtitle.M;
    drawText(ctx, subtitle, size / 2, size * 0.19, {
      fontSize: subtitleFontSize,
      fontWeight: "500",
      color: secondaryText,
      shadow: false
    });
  }
  
  // 4. Draw slogan (V5.2: with configurable size)
  const slogan = customSlogan || styleOptions.slogan || template?.slogan || "";
  if (slogan) {
    const sloganSizeKey = styleOptions.sloganSize || "M";
    const sloganFontSize = TEXT_SIZE_EXPORT_MAP.slogan[sloganSizeKey] || TEXT_SIZE_EXPORT_MAP.slogan.M;
    drawText(ctx, slogan, size / 2, size * 0.26, {
      fontSize: sloganFontSize,
      fontWeight: "600",
      color: primaryText + "EE",
      shadow: true
    });
  }
  
  // 5. Draw QR card (V5: perfectly centered) - BEFORE icons/emoji so they can overlap
  const qrInfo = await drawQRCard(ctx, size, qrToken, layout);
  
  // 6. Draw FIXED footer (V5.2: BIGGER BRANDING)
  drawFooter(ctx, size, palette, styleOptions.footerSize);
  
  // V6.6: Draw icons and emoji AFTER QR so they appear ON TOP
  // 7. Draw icons (with layoutOffsets support)
  if (styleOptions.showIcons !== false && styleOptions.activeIcons?.length > 0) {
    drawIcons(ctx, size, styleOptions.activeIcons, styleOptions.iconPosition || "top-right", palette, styleOptions.layoutOffsets);
  }
  
  // 8. Draw character emoji (decorative emoji) - ALWAYS if enabled
  if (styleOptions.characterEnabled && styleOptions.characterId) {
    // V6.5: Import characters from the same source as the editor
    // This list MUST match stickerCharacters.js for consistency
    const STICKER_CHARACTERS = [
      // Creativos
      { id: "hero_smile", emoji: "😎" },
      { id: "oops_face", emoji: "😅" },
      { id: "wink", emoji: "😉" },
      { id: "thinking", emoji: "🤔" },
      { id: "star_eyes", emoji: "🤩" },
      // Vehículos
      { id: "car_red", emoji: "🚗" },
      { id: "car_sport", emoji: "🏎️" },
      { id: "moto", emoji: "🏍️" },
      { id: "truck", emoji: "🚚" },
      { id: "parking", emoji: "🅿️" },
      // Emergencia
      { id: "siren", emoji: "🚨" },
      { id: "ambulance", emoji: "🚑" },
      { id: "warning", emoji: "⚠️" },
      { id: "sos", emoji: "🆘" },
      { id: "medical", emoji: "🏥" },
      // Mascotas
      { id: "dog", emoji: "🐕" },
      { id: "cat", emoji: "🐱" },
      { id: "paw", emoji: "🐾" },
      { id: "puppy_face", emoji: "🐶" },
      // Niños
      { id: "child", emoji: "🧒" },
      { id: "family", emoji: "👨‍👩‍👧" },
      { id: "baby", emoji: "👶" },
      { id: "angel", emoji: "👼" },
      // Viajes
      { id: "plane", emoji: "✈️" },
      { id: "luggage", emoji: "🧳" },
      { id: "backpack", emoji: "🎒" },
      { id: "world", emoji: "🌍" },
      // Objetos
      { id: "key", emoji: "🔑" },
      { id: "phone", emoji: "📱" },
      { id: "gift", emoji: "🎁" },
      { id: "heart", emoji: "❤️" },
      // Privacidad
      { id: "lock", emoji: "🔒" },
      { id: "shield", emoji: "🛡️" },
      { id: "spy", emoji: "🕵️" },
      // LATAM
      { id: "peru_flag", emoji: "🇵🇪" },
      { id: "handshake", emoji: "🤝" },
      { id: "fist", emoji: "👊" },
      { id: "call_me", emoji: "🤙" },
      // Legacy IDs (for backwards compatibility)
      { id: "car_wink", emoji: "🚗" },
      { id: "car_cool", emoji: "😎" },
      { id: "car_happy", emoji: "😊" },
      { id: "hand_wave", emoji: "👋" },
      { id: "sparkles", emoji: "✨" },
      { id: "star", emoji: "⭐" },
      { id: "thumbsup", emoji: "👍" },
      { id: "smile", emoji: "😊" },
      { id: "fire", emoji: "🔥" },
      { id: "rocket", emoji: "🚀" },
      { id: "check", emoji: "✅" }
    ];
    
    const character = STICKER_CHARACTERS.find(c => c.id === styleOptions.characterId);
    // V6.5: If not found, try to use the characterId directly as emoji (for custom emojis)
    const charEmoji = character?.emoji || styleOptions.characterId || "🚗";
    
    console.log("V6.6 Export - characterId:", styleOptions.characterId, "found:", !!character, "emoji:", charEmoji);
    
    // V6.4: Get position from layoutOffsets using NORMALIZED PERCENTAGES
    const emojiOffsets = styleOptions.layoutOffsets?.emoji || {};
    
    // V6.4: Calculate position - use percentages if available, otherwise fallback to pixels or defaults
    let emojiX, emojiY, emojiSize;
    
    if (emojiOffsets.xPercent !== undefined && emojiOffsets.yPercent !== undefined) {
      // V6.4: Use normalized percentage positions (PREFERRED)
      emojiX = emojiOffsets.xPercent * size + (emojiOffsets.widthPercent ? (emojiOffsets.widthPercent * size / 2) : 30);
      emojiY = emojiOffsets.yPercent * size + (emojiOffsets.heightPercent ? (emojiOffsets.heightPercent * size / 2) : 30);
      emojiSize = emojiOffsets.widthPercent ? emojiOffsets.widthPercent * size * 0.8 : 60;
    } else if (emojiOffsets.x !== undefined && emojiOffsets.y !== undefined) {
      // V6.4: Legacy pixel positions - scale from preview (420px) to export (1200px)
      const scaleFactor = size / 420;
      emojiX = emojiOffsets.x * scaleFactor + (emojiOffsets.width ? (emojiOffsets.width * scaleFactor / 2) : 30);
      emojiY = emojiOffsets.y * scaleFactor + (emojiOffsets.height ? (emojiOffsets.height * scaleFactor / 2) : 30);
      emojiSize = emojiOffsets.width ? emojiOffsets.width * scaleFactor * 0.8 : 60;
    } else {
      // V6.4: Default position (bottom-right quadrant)
      emojiX = size * 0.75;
      emojiY = size * 0.72;
      emojiSize = 60;
    }
    
    ctx.font = `${Math.max(40, emojiSize)}px system-ui`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(charEmoji, emojiX, emojiY);
  }

  return canvas;
};

// ============================================
// EXPORT FUNCTIONS
// ============================================

/**
 * Generate sticker as PNG blob
 */
export const generateStickerPNG = async (options) => {
  const canvas = await createStickerCanvas(options);
  
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/png", 1.0);
  });
};

/**
 * V5: Generate sticker as PDF with improved instructions
 */
export const generateStickerPDF = async (options) => {
  const canvas = await createStickerCanvas({ ...options, size: 800 });
  
  // Create PDF
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });
  
  // Add sticker image (centered, 7x7 cm for better print)
  const imgData = canvas.toDataURL("image/png", 1.0);
  const stickerSize = 70; // mm
  const x = (210 - stickerSize) / 2; // Center on A4
  const y = 25;
  
  pdf.addImage(imgData, "PNG", x, y, stickerSize, stickerSize);
  
  // Add instructions (idioma del dueño)
  const Li = pdfStrings();
  pdf.setFontSize(16);
  pdf.setTextColor(15, 23, 42);
  pdf.text(Li.title, 20, 115);
  
  pdf.setFontSize(11);
  pdf.setTextColor(100, 116, 139);
  const instructions = Li.instructions;
  
  let instructionY = 125;
  instructions.forEach((instruction) => {
    pdf.text(instruction, 20, instructionY);
    instructionY += 7;
  });
  
  // Footer (idioma del dueño)
  const L = pdfStrings();
  pdf.setFontSize(12);
  pdf.setTextColor(16, 185, 129);
  pdf.text(`🚗 ${L.footer_brand}`, 20, 185);
  
  pdf.setFontSize(9);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`${L.generated_on} ${new Date().toLocaleDateString(L.locale)}`, 20, 192);
  
  return pdf.output("blob");
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * V5 MAIN FUNCTION: Generate and download sticker
 * CRITICAL: Always fetches the latest QR first!
 */
export const generateSticker = async ({
  vehicleId,
  template,
  customSlogan,
  customTitle,
  customSubtitle,
  styleOptions = {},
  format = "png"
}) => {
  // 1. ALWAYS fetch latest QR (anti-cache)
  const qrData = await fetchLatestQR(vehicleId);
  
  if (!qrData.qr_token && !qrData.vehicles?.[0]?.qr_token) {
    throw new Error("No se encontró el código QR");
  }
  
  const qrToken = qrData.qr_token || qrData.vehicles?.[0]?.qr_token;
  const qrVersion = qrData.qr_payload_version || qrData.vehicles?.[0]?.qr_payload_version;
  
  // 2. Generate sticker
  const options = {
    template,
    qrToken,
    customSlogan,
    customTitle,
    customSubtitle,
    styleOptions
  };
  
  let blob;
  let filename;
  
  if (format === "pdf") {
    blob = await generateStickerPDF(options);
    filename = `avisocar-sticker-${template?.id || 'custom'}-v${qrVersion}.pdf`;
  } else {
    blob = await generateStickerPNG(options);
    filename = `avisocar-sticker-${template?.id || 'custom'}-v${qrVersion}.png`;
  }
  
  // 3. Download
  downloadBlob(blob, filename);
  
  return {
    success: true,
    format,
    qr_token: qrToken,
    qr_version: qrVersion
  };
};

/**
 * V5: Generate preview canvas for live display (no download)
 */
export const generatePreviewCanvas = async ({ template, qrToken, customSlogan, customTitle, customSubtitle, styleOptions = {}, size = 300 }) => {
  return createStickerCanvas({ template, qrToken, customSlogan, customTitle, customSubtitle, styleOptions, size });
};

export default {
  generateSticker,
  generateStickerPNG,
  generateStickerPDF,
  generatePreviewCanvas,
  fetchLatestQR,
  downloadBlob
};
